import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserService } from '../utility/services/user';
import { CommonUtility } from '../utility/common';
import { AccountService } from '../utility/services/account';
import type { IAuthResponse, IUser, IOrganization } from 'types';

// --- Context Interface ---
interface IAuthContext {
  loading: boolean;
  user: IUser | null;
  isAuthenticated: boolean | null;
  logout: () => void;
  updateUserDetails: (userDetails: IUser) => void;
  refreshUserData: () => Promise<void>;
  setUserData: (userObject: IAuthResponse) => void;
  currentOrg: IOrganization | null;
  isUser: boolean;
}

// --- Props Interface ---
interface IAuthProviderProps {
  children: ReactNode;
}

// --- Default Context ---
const AuthContext = createContext<IAuthContext>({} as IAuthContext);

export function AuthProvider({ children }: IAuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const setUserData = (userObject: IAuthResponse) => {
    const authenticated = !!userObject.accessToken;
    setIsAuthenticated(authenticated);

    if (authenticated) {
      const user = userObject.user;
      setUser(user);
    }
  };

  const refreshUserData = async () => {
    try {
      const res = (await AccountService.get()) as IAuthResponse;
      UserService.storeUser(res);
      setUserData(res);
    } catch (error) {
      logout();

      setTimeout(() => {
        const query = {
          redirectUrl: `${window.location.pathname}${window.location.search}`,
        };
        navigate('/login', {
          state: { query },
        });
      }, 300);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    if (
      !CommonUtility.isValidArray(user?.organizations) &&
      !pathname.includes('onboarding')
    ) {
      navigate('/app/onboarding');
    }
  }, [isAuthenticated, user, pathname, navigate]);

  useEffect(() => {
    const loadToken = async () => {
      const authenticated = UserService.isAuthenticated();
      if (authenticated) {
        await refreshUserData();
      } else {
        setIsAuthenticated(false);
      }
      setLoading(false);
    };
    loadToken();
  }, []);

  const logout = () => {
    setIsAuthenticated(false);
    UserService.logout();
  };

  const updateUserDetails = (userDetails: IUser) => {
    setUser(userDetails);
    const updatedUser = UserService.getUser();
    if (updatedUser) {
      updatedUser.user = userDetails;
      UserService.storeUser(updatedUser);
    }
  };

  const currentOrg = useMemo<IOrganization | null>(() => {
    if (!user?.organizations?.length) return null;
    return user.organizations.find(x => x._id === user.organizationId) || null;
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        loading,
        user,
        isAuthenticated,
        logout,
        updateUserDetails,
        refreshUserData,
        setUserData,
        currentOrg,
        isUser: currentOrg?.role === 'user',
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = (): IAuthContext => useContext(AuthContext);
